import { useMemo, useSyncExternalStore } from 'react'

import type { Timestamp } from '@bufbuild/protobuf/wkt'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import {
  decodeReadMark,
  encodeReadMark,
  isNewerReadMark,
} from '~/models/announcementUnread'

export const ANNOUNCEMENT_READ_STORAGE_KEY =
  'ebalance.announcements.lastReadPublishedAt'

// localStorage に書けない環境 (Safari private mode 等) でだけ使う退避先。
// 書けた環境で常に持つと、テストの `localStorage.clear()` や他タブの消去の後も
// 古い既読が残って見える。
let memoryFallback: string | undefined
const listeners = new Set<() => void>()

// 書き込みだけが失敗する環境 (容量超過) では古い保存値が読めてしまうため、
// 退避先を優先せず新しい方を採る (退避先を常に優先すると、他タブがその後に
// 書いたより新しい既読を取りこぼす)。
const readRaw = (): string | undefined => {
  const stored = getLocalStorageItem(ANNOUNCEMENT_READ_STORAGE_KEY)
  if (memoryFallback === undefined) return stored
  return isNewerReadMark(decodeReadMark(memoryFallback), decodeReadMark(stored))
    ? memoryFallback
    : stored
}

const notify = (): void => {
  for (const listener of listeners) listener()
}

const onStorage = (event: StorageEvent): void => {
  // key === null は他タブの localStorage.clear()
  if (event.key === ANNOUNCEMENT_READ_STORAGE_KEY || event.key === null)
    notify()
}

const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0) window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}

const markRead = (publishedAt: Timestamp): void => {
  if (!isNewerReadMark(publishedAt, decodeReadMark(readRaw()))) return
  const raw = encodeReadMark(publishedAt)
  setLocalStorageItem(ANNOUNCEMENT_READ_STORAGE_KEY, raw)
  if (getLocalStorageItem(ANNOUNCEMENT_READ_STORAGE_KEY) !== raw)
    memoryFallback = raw
  notify()
}

/**
 * リリース告知の既読 (最後に読んだ告知の `published_at`) を読み書きする唯一の hook。
 *
 * サイドメニューの未読ドットと一覧 page が同じ module-level store を購読するため、
 * 一覧での既読化は同じ描画サイクルでドットに反映される。他タブの既読は `storage`
 * event で追従する。`markRead` は保存済みより新しい時だけ書く (古い値で巻き戻さない)。
 *
 * 永続化先はブラウザの localStorage で、ログアウトでは消さない。既読をサーバーに
 * 持たせる時 (map #8571 候補 4) は、この hook の中身だけを差し替える。
 */
export function useAnnouncementReadState(): {
  lastReadPublishedAt: Timestamp | undefined
  markRead: (publishedAt: Timestamp) => void
} {
  const raw = useSyncExternalStore(subscribe, readRaw)
  const lastReadPublishedAt = useMemo(() => decodeReadMark(raw), [raw])
  return { lastReadPublishedAt, markRead }
}
